import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from './avatar';

/**
 * AvatarUpload — avatar preview + "Change photo" picker.
 *
 * Two modes:
 *   • onUpload(file)->Promise  — upload immediately (edit: we already have an id).
 *   • onSelect(file)           — just hand the file up; the parent uploads later
 *                                (create: no id yet, upload on form submit).
 * Either/both may be passed. Shows an instant local preview + validation; falls
 * back to initials via <Avatar> when there's no photo yet.
 *
 * Props: { name, src, size = 76, square, onUpload, onSelect, disabled,
 *          uploadLabel = 'Upload photo', changeLabel = 'Change photo' }
 */
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;

const CameraIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
    </svg>
);

const AvatarUpload = ({ name = '', src, size = 76, square = false, onUpload, onSelect, disabled = false, uploadLabel, changeLabel }) => {
    const { t } = useTranslation();
    const _uploadLabel = uploadLabel || t('avatarUpload.uploadPhoto', 'Upload photo');
    const _changeLabel = changeLabel || t('avatarUpload.changePhoto', 'Change photo');
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const pick = () => { if (!busy && !disabled && inputRef.current) inputRef.current.click(); };

    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) { setError(t('avatarUpload.invalidFormat', 'Use a PNG, JPG, WEBP or GIF image.')); return; }
        if (file.size > MAX_BYTES) { setError(t('avatarUpload.fileTooLarge', 'Image must be under 5 MB.')); return; }
        setError('');

        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(file);

        if (typeof onSelect === 'function') onSelect(file);

        if (typeof onUpload !== 'function') return;
        setBusy(true);
        try {
            await onUpload(file);
        } catch (err) {
            setError(err?.message || t('avatarUpload.uploadFailed', 'Upload failed. Please try again.'));
            setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="sbr-avatarup">
            <button type="button" className="sbr-avatarup-img" onClick={pick} disabled={disabled} title={_changeLabel}>
                <Avatar src={preview || src} name={name} size={size} square={square} />
                <span className="sbr-avatarup-cam" aria-hidden="true"><CameraIcon /></span>
            </button>
            <div className="sbr-avatarup-tx">
                <button type="button" className="sbr-avatarup-btn" onClick={pick} disabled={disabled || busy}>
                    {busy ? t('avatarUpload.uploading', 'Uploading...') : (src || preview ? _changeLabel : _uploadLabel)}
                </button>
                <span className="sbr-avatarup-hint">{t('avatarUpload.formatHint', 'PNG, JPG or WEBP · up to 5 MB')}</span>
                {error && <span className="sbr-avatarup-err">{error}</span>}
            </div>
            <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFile} hidden />
        </div>
    );
};

export default AvatarUpload;
